import { useAuthStore } from '@/stores/auth'
import axios from 'axios'

// Workflow d'expédition partagé (CommandesPage.vue / CommandeDetailsPage.vue)
// Basé sur la nouvelle API UGO (Upela) — voir API_UGO_FRONTEND.md
const FROM_ADDRESS_STORAGE_KEY = 'guepard_ship_from_address'

// Adresse d'expédition par défaut (entrepôt Djamila Transit) — pré-remplie, reste éditable par le client
const DEFAULT_FROM_ADDRESS = {
  name: 'Djamila transit',
  company: 'Djamila Transit',
  address_line_1: 'avenue de la république',
  house_number: '59',
  postal_code: '95200',
  city: 'Arnouville',
  country_code: 'FR',
  phone_number: '+337 45 96 39 07',
  email: 'djamilatransit@gmail.com'
}

export default {
  data() {
    return {
      shipWorkflow: {
        show: false,
        step: 1, // 1: Comparateur, 2: Récapitulatif, 3: Paiement, 4: Suivi
        loading: false,
        error: null,
        orderId: null,
        currentOrder: null,
        shipment: {
          uuid: null,
          status: 'pending',
          tracking_number: null,
          tracking_numbers: [],
          label_url: null,
          label_urls: [],
          public_tracking_url: null,
          shipping_method_id: null,
          weight: 0.1
        },
        payments: [],
        methods: [],
        searchedOffers: false,
        selectedMethod: null,
        colis: [],
        filters: {
          carrier: '',
          sortBy: '',
          maxPrice: null,
          country: 'FR'
        },
        // Adresse du destinataire (to_address / ship_to)
        address: {
          name: '',
          address_line_1: '',
          house_number: '',
          postal_code: '',
          city: '',
          country_code: 'FR',
          phone_number: '',
          email: ''
        },
        // Adresse d'expédition saisie par le client (ship_from) — entrepôt / point de dépôt
        fromAddress: { ...DEFAULT_FROM_ADDRESS },
        tracking: {
          tracking_number: null,
          label_url: null,
          statuses: [],
          statusLabel: '',
          notFound: false
        },
        stripe: {
          instance: null,
          elements: null,
          card: null,
          clientSecret: null,
          initToken: 0,
          paymentIntentId: null,
          paymentId: null, // id du paiement local renvoyé par create-intent-for-order (pour /verify)
          processing: false,
          error: null
        },
        toast: {
          show: false,
          message: '',
          type: 'error'
        },
        pollingInterval: null,
        pollingTimer: null
      }
    }
  },
  computed: {
    auth() {
      return useAuthStore()
    },
    isProduction() {
      return process.env.NODE_ENV === 'production'
    },
    rawMethods() {
      return this.shipWorkflow.methods || []
    },
    successfulPayment() {
      return this.shipWorkflow.payments?.find(p => p.status === 'succeeded')
    },
    parcelGroups() {
      const colis = this.shipWorkflow.colis || []
      const groups = []
      for (let i = 0; i < colis.length; i += 2) {
        groups.push(colis.slice(i, i + 2))
      }
      return groups
    },
    consolidationFee() {
      return this.parcelGroups.reduce((total, group, idx) => {
        // Premier regroupement : 6€, les suivants : 3,60€
        const fee = idx === 0 ? 6 : 3.6
        return total + fee
      }, 0)
    },
    progressPercentage() {
      const statuses = this.shipWorkflow.tracking?.statuses || []
      const shipmentStatus = String(this.shipWorkflow.shipment?.status || '').toLowerCase()

      if (shipmentStatus === 'delivered' || statuses.some(s => s.message.toLowerCase().includes('livré') || s.message.toLowerCase().includes('delivered'))) {
        return 100
      }

      if (shipmentStatus === 'cancelled' || shipmentStatus === 'delivery_failed') {
        return 0
      }

      if (statuses.length === 0) return 0

      const lastStatus = (statuses[0]?.message || '').toLowerCase()

      if (lastStatus.includes('livré') || lastStatus.includes('delivered')) return 100
      if (lastStatus.includes('transit') || lastStatus.includes('route') || lastStatus.includes('tri') || lastStatus.includes('sorting')) return 75
      if (lastStatus.includes('pris') || lastStatus.includes('picked') || lastStatus.includes('charge')) return 50
      if (lastStatus.includes('prêt') || lastStatus.includes('ready') || lastStatus.includes('étiquette') || lastStatus.includes('announced')) return 25

      return Math.min(100, Math.max(15, (statuses.length / 5) * 100))
    },
    filteredMethods() {
      const methods = this.rawMethods
      if (methods.length === 0) return []

      let list = [...methods]

      if (this.shipWorkflow.filters.carrier) {
        const q = this.shipWorkflow.filters.carrier.toLowerCase()
        list = list.filter(m =>
          (m.carrier?.toLowerCase() || '').includes(q) ||
          (m.name?.toLowerCase() || '').includes(q)
        )
      }

      if (this.shipWorkflow.filters.maxPrice) {
        list = list.filter(m => (m.final_price || m.price || 0) <= this.shipWorkflow.filters.maxPrice)
      }

      if (this.shipWorkflow.filters.sortBy === 'price') {
        list.sort((a, b) => (a.final_price || a.price || 0) - (b.final_price || b.price || 0))
      } else if (this.shipWorkflow.filters.sortBy === 'delay') {
        list.sort((a, b) => (a.delivery_time || 0) - (b.delivery_time || 0))
      }

      return list
    }
  },
  mounted() {
    this.initStripeInstance()

    this.shipWorkflow.pollingTimer = setInterval(() => {
      if (this.shipWorkflow.show && this.shipWorkflow.step === 4 && this.shipWorkflow.shipment?.uuid) {
        this.getTracking()
      }
    }, 30000)
  },
  beforeUnmount() {
    if (this.shipWorkflow.pollingTimer) {
      clearInterval(this.shipWorkflow.pollingTimer)
    }
    if (this.shipWorkflow.pollingInterval) {
      clearInterval(this.shipWorkflow.pollingInterval)
    }
  },
  methods: {
    // --- Rafraîchit les données de la page hôte après une action (duck-typing) ---
    refreshHostData() {
      if (typeof this.fetchDetails === 'function') this.fetchDetails()
      else if (typeof this.fetchCommandes === 'function') this.fetchCommandes()
    },

    async initStripeInstance() {
      if (this.shipWorkflow.stripe.instance) return true

      try {
        if (!window.Stripe) {
          await new Promise((resolve) => {
            const check = setInterval(() => {
              if (window.Stripe) {
                clearInterval(check)
                resolve()
              }
            }, 100)
            setTimeout(() => { clearInterval(check); resolve() }, 3000)
          })
        }

        if (!window.Stripe) return false

        // La clé publique DOIT être du même mode (test/live) que la clé secrète
        // utilisée par le backend pour créer le PaymentIntent, sinon Stripe refuse
        // silencieusement de monter le Payment Element.
        // Source : variable d'env (VUE_APP_STRIPE_PUBLISHABLE_KEY) puis endpoint
        // backend /payments/config. AUCUN fallback codé en dur : une clé live
        // en dur permettrait de créer de vrais paiements depuis un env de test.
        const res = await axios.get('/payments/config').catch(() => ({ data: {} }))
        const envKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY
        const backendKey = res.data.publishable_key || res.data.publicKey || res.data.public_key
        const stripeKey = envKey || backendKey

        if (!stripeKey) {
          console.error('🚨 Aucune clé publique Stripe : définir VUE_APP_STRIPE_PUBLISHABLE_KEY ou l\'endpoint /payments/config.')
          this.shipWorkflow.stripe.error = "Paiement indisponible : clé publique Stripe non configurée."
          return false
        }
        const keySource = envKey ? 'VUE_APP_STRIPE_PUBLISHABLE_KEY' : '/payments/config'
        console.info(
          `💳 Stripe: clé "${stripeKey.slice(0, 25)}…" (mode ${stripeKey.startsWith('pk_test_') ? 'TEST' : 'LIVE'}, source: ${keySource})`
        )

        this.shipWorkflow.stripe.instance = window.Stripe(stripeKey)
        return true
      } catch (e) {
        console.error('🚨 Erreur lors de l\'initialisation Stripe:', e)
        return false
      }
    },

    cleanupStripeElements() {
      const stripe = this.shipWorkflow.stripe
      const card = stripe?.card

      if (stripe) {
        stripe.card = null
        stripe.elements = null
      }

      if (card) {
        try {
          if (typeof card.unmount === 'function') card.unmount()
        } catch (_) { /* noop */ }
        try {
          if (typeof card.destroy === 'function') card.destroy()
        } catch (_) { /* noop */ }
      }
    },

    // --- Normalise l'adresse du destinataire au format attendu par l'API UGO ---
    buildAddressPayload() {
      const addr = this.shipWorkflow.address
      const street = [addr.house_number, addr.address_line_1].filter(Boolean).join(' ').trim()
      const country = String(addr.country_code || '')
      // UGO exige un `company` non vide → repli sur le nom si aucune société saisie
      const company = String(addr.company || addr.name || 'Particulier')
      return {
        lastname: String(addr.name || ''),
        firstname: null,
        address1: street,
        city: String(addr.city || ''),
        postcode: String(addr.postal_code || ''),
        country_code: country,
        country, // alias attendu par l'étape /ship
        email: String(addr.email || ''),
        phone: String(addr.phone_number || ''),
        company
      }
    },

    // --- Normalise l'adresse d'expédition (ship_from) saisie par le client ---
    buildFromAddressPayload() {
      const addr = this.shipWorkflow.fromAddress
      const street = [addr.house_number, addr.address_line_1].filter(Boolean).join(' ').trim()
      const country = String(addr.country_code || '')
      const company = String(addr.company || addr.name || 'Expéditeur')
      return {
        lastname: String(addr.name || ''),
        firstname: null,
        address1: street,
        city: String(addr.city || ''),
        postcode: String(addr.postal_code || ''),
        country_code: country,
        country, // alias attendu par l'étape /ship
        email: String(addr.email || ''),
        phone: String(addr.phone_number || ''),
        company
      }
    },

    // --- Se souvient de la dernière adresse d'expédition saisie (confort, par navigateur) ---
    loadSavedFromAddress() {
      try {
        const raw = window.localStorage.getItem(FROM_ADDRESS_STORAGE_KEY)
        return raw ? JSON.parse(raw) : null
      } catch (_) {
        return null
      }
    },

    saveFromAddress() {
      try {
        window.localStorage.setItem(FROM_ADDRESS_STORAGE_KEY, JSON.stringify(this.shipWorkflow.fromAddress))
      } catch (_) { /* noop */ }
    },

    // --- Transforme les offres UGO (offers[] / shipping_options[]) en méthodes affichables ---
    mapOffersToMethods(offers) {
      const serviceFee = 6
      const consolidationFeeValue = this.consolidationFee || 0

      return (offers || []).map((offer, idx) => {
        const basePrice = parseFloat(offer.price_ti ?? offer.price_ttc ?? offer.price ?? offer.price_te ?? 0)
        const carrierName = offer.carrier?.name || offer.carrier_name || offer.service_name || offer.name || 'Transporteur'

        return {
          id: offer.id || `offer_${idx}`,
          name: offer.service_name || offer.name || carrierName,
          carrier: carrierName,
          carrier_logo: offer.carrier?.logo || offer.carrier_logo || null,
          delivery_time: offer.delivery_time || offer.transit_time || offer.delay || null,
          max_weight: offer.max_weight || null,
          original_price: basePrice,
          service_fee: serviceFee,
          consolidation_fee: consolidationFeeValue,
          final_price: basePrice + serviceFee + consolidationFeeValue,
          raw: offer
        }
      })
    },

    // --- Workflow Expédition (API UGO) ---
    async startShipping(commande) {
      this.shipWorkflow.show = true
      this.shipWorkflow.step = 1
      this.shipWorkflow.loading = true
      this.shipWorkflow.error = null
      this.shipWorkflow.orderId = commande.id
      this.shipWorkflow.currentOrder = commande
      this.shipWorkflow.stripe.paymentIntentId = null

      const initialCountry = commande.destination_code || commande.commande?.destination_code || 'FR'
      this.shipWorkflow.filters.country = initialCountry

      const root = commande
      const nested = commande.commande || {}

      this.shipWorkflow.address = {
        name: root.nom_destinateur || nested.nom_destinateur || root.name || nested.name || this.auth.user?.name || '',
        address_line_1: root.adresse_destinateur || nested.adresse_destinateur || root.address || nested.address || '',
        house_number: root.house_number || nested.house_number || '',
        postal_code: root.code_postal_destinateur || nested.code_postal_destinateur || root.postal_code || nested.postal_code || '',
        city: root.ville_destinateur || nested.ville_destinateur || root.destination || nested.destination || root.city || nested.city || '',
        country_code: root.destination_code || nested.destination_code || initialCountry,
        phone_number: root.numero_destinateur || nested.numero_destinateur || root.telephone_destinateur || nested.telephone_destinateur || root.phone || nested.phone || '',
        email: root.email_destinateur || nested.email_destinateur || root.email || nested.email || this.auth.user?.email || ''
      }

      const colisList = root.colis || nested.colis || []

      this.shipWorkflow.colis = colisList.map(c => ({
        description: String(c.description || c.nom || c.name || 'Article de colis'),
        nombre: parseInt(c.nombre || c.quantity || 1),
        weight: parseFloat(c.weight || c.masse || c.poids || 0.1),
        valeur: parseFloat(c.valeur || c.valeur_colis || c.prix || c.price || 0),
        hs_code: String(c.hs_code || ''),
        origin_country: String(c.origin_country || c.pays_origine || 'FR'),
        x: parseFloat(c.long || c.x || c.longueur || 0) || undefined,
        y: parseFloat(c.larg || c.y || c.largeur || 0) || undefined,
        z: parseFloat(c.haut || c.z || c.hauteur || 0) || undefined
      }))

      const parseWeight = (val) => {
        if (val === undefined || val === null || val === '') return 0
        const cleaned = String(val).replace(',', '.').replace(/[^\d.]/g, '')
        return parseFloat(cleaned) || 0
      }

      const colisWeight = colisList.reduce((sum, item) => sum + parseWeight(item.masse || item.weight || item.poids), 0)
      const detailsWeight = parseWeight(root.details?.poids_total || nested.details?.poids_total || root.poids_total || nested.poids_total)
      const rootWeight = parseWeight(root.weight || nested.weight || root.poids || nested.poids || root.masse || nested.masse)

      let finalWeight = 0
      if (colisWeight > 0) finalWeight = colisWeight
      else if (detailsWeight > 0) finalWeight = detailsWeight
      else if (rootWeight > 0) finalWeight = rootWeight
      if (finalWeight <= 0) finalWeight = 0.1

      this.shipWorkflow.shipment = {
        weight: finalWeight,
        id: root.shipment?.id || nested.shipment?.id,
        uuid: root.shipment?.uuid || nested.shipment?.uuid,
        status: root.shipment?.status || nested.shipment?.status || 'pending',
        payments: root.shipment?.payments || nested.shipment?.payments || []
      }

      this.shipWorkflow.payments = this.shipWorkflow.shipment.payments

      const existingShipment = root.shipment || nested.shipment
      const isPaid = this.shipWorkflow.payments?.some(p => p.status === 'succeeded')

      // "Réellement expédié" = on a un numéro de suivi. Un statut "shipped" sans
      // numéro (données de test incohérentes) ne suffit pas : il faut relancer /ship.
      const alreadyShipped = !!(existingShipment && (existingShipment.tracking_number || existingShipment.tracking_numbers?.length))

      if (existingShipment && (alreadyShipped || isPaid)) {
        this.shipWorkflow.step = 4
        this.shipWorkflow.selectedMethod = {
          name: existingShipment.ugo_carrier_name || existingShipment.shipping_method_id,
          carrier: existingShipment.ugo_carrier_name || existingShipment.shipping_method_id
        }
        this.shipWorkflow.shipment = {
          ...this.shipWorkflow.shipment,
          ...existingShipment,
          weight: this.shipWorkflow.shipment.weight
        }
        this.shipWorkflow.tracking = {
          tracking_number: existingShipment.tracking_number,
          label_url: existingShipment.label_url,
          statuses: [],
          statusLabel: '',
          notFound: false
        }
        this.shipWorkflow.fromAddress = this.loadSavedFromAddress() || { ...DEFAULT_FROM_ADDRESS }
        this.shipWorkflow.loading = false

        // Paiement OK mais expédition pas encore finalisée → on déclenche /ship
        // (au lieu de rester bloqué sur "Préparation des étiquettes…" indéfiniment).
        if (!alreadyShipped && isPaid) {
          this.shipParcel()
        } else {
          this.getTracking()
        }
        return
      }

      this.shipWorkflow.userId = root.user_id || nested.user_id || this.auth.user?.id

      // L'adresse d'expédition (ship_from) est saisie par le client : on préremplit
      // avec la dernière valeur connue (confort), l'utilisateur doit ensuite lancer la recherche.
      this.shipWorkflow.fromAddress = this.loadSavedFromAddress() || { ...DEFAULT_FROM_ADDRESS }
      this.shipWorkflow.methods = []
      this.shipWorkflow.searchedOffers = false
      this.shipWorkflow.loading = false
    },

    // --- Vérifie qu'une adresse contient tous les champs obligatoires, renvoie les libellés manquants ---
    missingAddressFields(addr) {
      const requiredFields = {
        name: 'Nom complet',
        address_line_1: 'Adresse (rue)',
        house_number: 'Numéro',
        postal_code: 'Code postal',
        city: 'Ville',
        phone_number: 'Téléphone',
        email: 'Email'
      }

      return Object.entries(requiredFields)
        .filter(([key]) => !String(addr[key] || '').trim())
        .map(([, label]) => label)
    },

    // --- Valide les adresses (expéditeur + destinataire) puis déclenche la recherche d'offres ---
    async searchOffers() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

      const missingFrom = this.missingAddressFields(this.shipWorkflow.fromAddress)
      if (missingFrom.length > 0) {
        this.showToast(`Adresse d'expédition incomplète : ${missingFrom.join(', ')}`, 'error')
        return
      }
      if (!emailRegex.test(this.shipWorkflow.fromAddress.email)) {
        this.showToast("L'adresse email de l'expéditeur n'est pas valide.", 'error')
        return
      }

      const missingTo = this.missingAddressFields(this.shipWorkflow.address)
      if (missingTo.length > 0) {
        this.showToast(`Adresse du destinataire incomplète : ${missingTo.join(', ')}`, 'error')
        return
      }
      if (!emailRegex.test(this.shipWorkflow.address.email)) {
        this.showToast("L'adresse email du destinataire n'est pas valide.", 'error')
        return
      }

      this.saveFromAddress()
      this.shipWorkflow.searchedOffers = true
      await this.loadShippingComparator()
    },

    // --- Étape 1 : crée/rafraîchit l'expédition locale et récupère les offres UGO ---
    async loadShippingComparator() {
      this.shipWorkflow.loading = true
      this.shipWorkflow.error = null
      try {
        const payload = {
          order_id: String(this.shipWorkflow.orderId),
          external_order_id: String(this.shipWorkflow.orderId),
          parcels: (this.shipWorkflow.colis || []).map(c => ({
            weight: c.weight || 0.1,
            x: c.x,
            y: c.y,
            z: c.z
          })),
          ship_from: this.buildFromAddressPayload(),
          to_address: this.buildAddressPayload(),
          label_count: 2
        }

        const res = await axios.post('/ugo/shipments/from-order', payload)

        if (!res.data?.success) {
          throw new Error(res.data?.error || res.data?.message || 'Impossible de récupérer les offres de transport.')
        }

        const shipmentData = res.data.shipment || {}
        this.shipWorkflow.shipment.uuid = shipmentData.uuid || shipmentData.id || this.shipWorkflow.shipment.uuid
        this.shipWorkflow.shipment.id = shipmentData.id || this.shipWorkflow.shipment.id
        this.shipWorkflow.shipment.status = shipmentData.status || this.shipWorkflow.shipment.status
        this.shipWorkflow.totalFees = res.data.total_fees

        const offers = res.data.shipping_options?.length ? res.data.shipping_options : (res.data.offers || [])
        this.shipWorkflow.methods = this.mapOffersToMethods(offers)
      } catch (e) {
        console.error('🚨 Erreur récupération des offres UGO:', e)
        this.shipWorkflow.error = e.response?.data?.error || e.response?.data?.message || e.message || 'Impossible de récupérer les offres de transport.'
      } finally {
        this.shipWorkflow.loading = false
      }
    },

    async updateCarriers() {
      await this.startShipping(this.shipWorkflow.currentOrder)
    },

    closeShipWorkflow() {
      if (this.shipWorkflow.pollingInterval) {
        clearInterval(this.shipWorkflow.pollingInterval)
      }
      this.shipWorkflow.pollingInterval = null

      this.shipWorkflow.stripe.initToken = (this.shipWorkflow.stripe.initToken || 0) + 1
      this.cleanupStripeElements()

      this.shipWorkflow.show = false
      this.shipWorkflow.step = 1
      this.shipWorkflow.selectedMethod = null
      this.shipWorkflow.error = null
      this.shipWorkflow.stripe.clientSecret = null
      this.shipWorkflow.stripe.processing = false
      this.shipWorkflow.stripe.error = null
      this.refreshHostData()
    },

    selectMethod(method) {
      this.shipWorkflow.selectedMethod = method
      this.shipWorkflow.error = null
    },

    async confirmCarrier() {
      if (!this.shipWorkflow.selectedMethod) return

      const weight = parseFloat(this.shipWorkflow.shipment?.weight || 0)
      const maxWeight = parseFloat(this.shipWorkflow.selectedMethod.max_weight || 0)

      if (maxWeight > 0 && weight > maxWeight) {
        this.shipWorkflow.error = `Le poids de votre commande (${weight} kg) dépasse la limite autorisée par ce transporteur (${maxWeight} kg). Veuillez choisir une autre méthode.`
        return
      }

      this.shipWorkflow.error = null
      this.shipWorkflow.step = 2
    },

    // --- Étape 2→3 : confirme l'offre puis initialise le paiement Stripe ---
    async initStripeForm() {
      this.shipWorkflow.loading = true
      this.shipWorkflow.stripe.error = null

      try {
        // Les adresses (expéditeur + destinataire) ont déjà été validées à l'étape 1
        // (recherche d'offres) : plus besoin de re-valider ici.
        const shipmentId = this.shipWorkflow.shipment?.uuid || this.shipWorkflow.shipment?.id
        if (!shipmentId) {
          throw new Error("Aucune expédition en cours. Merci de resélectionner un transporteur.")
        }

        // 🟢 ÉTAPE 1 (UGO) : Confirmer l'offre choisie
        const totalTtc = parseFloat(this.shipWorkflow.selectedMethod?.final_price || this.shipWorkflow.selectedMethod?.original_price || 0)
        const payloadSelectMethod = {
          external_order_id: String(this.shipWorkflow.orderId),
          offer: this.shipWorkflow.selectedMethod?.raw,
          total_ttc: totalTtc
        }

        const selectRes = await axios.post('/ugo/shipments/select-method', payloadSelectMethod)

        if (!selectRes.data?.success) {
          const details = selectRes.data?.data?.details?.map(d => `${d.property_path}: ${d.violation}`).join(', ')
          throw new Error([selectRes.data?.error || selectRes.data?.message || 'Erreur lors de la sélection du transporteur.', details].filter(Boolean).join(' — '))
        }

        // 🟢 ÉTAPE 2 : Créer l'intention de paiement
        const amount = Number((totalTtc || 0).toFixed(2))
        if (isNaN(amount) || amount <= 0) {
          throw new Error(`Montant invalide pour le paiement: ${totalTtc}. Veuillez choisir une autre méthode.`)
        }

        const payloadIntent = {
          external_order_id: String(this.shipWorkflow.orderId),
          shipment_id: shipmentId,
          amount,
          currency: 'eur'
        }

        const response = await axios.post('/payments/create-intent-for-order', payloadIntent)
        const payload = response.data
        if (!payload?.success) {
          throw new Error(payload?.error || payload?.message || 'Erreur lors de l\'initialisation du paiement.')
        }

        this.shipWorkflow.stripe.clientSecret = payload.client_secret
        this.shipWorkflow.stripe.paymentId = payload.payment_id || payload.payment?.id || null

        this.shipWorkflow.stripe.initToken = (this.shipWorkflow.stripe.initToken || 0) + 1
        const initToken = this.shipWorkflow.stripe.initToken
        this.shipWorkflow.step = 3

        this.$nextTick(() => this.mountPaymentElement(initToken))
      } catch (e) {
        console.error('🚨 Erreur préparation paiement:', e)
        const backendError = e.response?.data?.error || e.response?.data?.message || e.message
        this.shipWorkflow.stripe.error = `🚨 ${backendError}`
      } finally {
        this.shipWorkflow.loading = false
      }
    },

    // --- Monte le Stripe Payment Element dans #payment-element (avec retries) ---
    async mountPaymentElement(initToken, attempt = 0) {
      if (initToken !== this.shipWorkflow.stripe.initToken) return
      if (!this.shipWorkflow.show || this.shipWorkflow.step !== 3) return

      if (!this.shipWorkflow.stripe.instance) {
        await this.initStripeInstance()
      }
      if (!this.shipWorkflow.stripe.instance) {
        this.shipWorkflow.stripe.error = "Stripe n'a pas pu être initialisé (clé publique manquante ou invalide)."
        return
      }
      if (!this.shipWorkflow.stripe.clientSecret) {
        this.shipWorkflow.stripe.error = "Aucun client_secret reçu du backend pour ce paiement."
        return
      }

      // Le div #payment-element peut ne pas être encore rendu : on retente brièvement.
      const target = document.querySelector('#payment-element')
      if (!target) {
        if (attempt < 20) {
          setTimeout(() => this.mountPaymentElement(initToken, attempt + 1), 100)
        } else {
          this.shipWorkflow.stripe.error = "Impossible d'afficher le formulaire de carte (élément introuvable)."
        }
        return
      }

      try {
        console.info('💳 Montage Payment Element avec client_secret:', String(this.shipWorkflow.stripe.clientSecret).slice(0, 30), '…')
        this.cleanupStripeElements()
        this.shipWorkflow.stripe.elements = this.shipWorkflow.stripe.instance.elements({
          clientSecret: this.shipWorkflow.stripe.clientSecret
        })
        this.shipWorkflow.stripe.card = this.shipWorkflow.stripe.elements.create('payment')
        this.shipWorkflow.stripe.card.on('loaderror', (event) => {
          console.error('🚨 Stripe Payment Element loaderror:', event?.error)
          this.shipWorkflow.stripe.error = event?.error?.message
            || "Le formulaire de paiement n'a pas pu se charger (vérifiez que la clé publique et le client_secret sont dans le même mode test/live)."
        })
        this.shipWorkflow.stripe.card.mount('#payment-element')
      } catch (e) {
        console.error('🚨 Échec montage Payment Element:', e)
        this.shipWorkflow.stripe.error = e.message || "Échec de l'affichage du formulaire de carte."
      }
    },

    async confirmPayment() {
      if (this.shipWorkflow.stripe.processing) return

      if (this.shipWorkflow.shipment?.status === 'shipped' || this.shipWorkflow.tracking?.label_url) {
        this.shipWorkflow.step = 4
        return
      }

      this.shipWorkflow.stripe.processing = true
      this.shipWorkflow.stripe.error = null

      try {
        const { paymentIntent, error } = await this.shipWorkflow.stripe.instance.confirmPayment({
          elements: this.shipWorkflow.stripe.elements,
          confirmParams: {
            return_url: window.location.href
          },
          redirect: 'if_required'
        })

        // L'id à utiliser pour /verify est le payment_id LOCAL (renvoyé par
        // create-intent-for-order), pas l'id du PaymentIntent Stripe (pi_...).
        const verifyId = this.shipWorkflow.stripe.paymentId

        if (error) {
          if (error.payment_intent?.status === 'succeeded') {
            this.shipWorkflow.stripe.paymentIntentId = error.payment_intent.id
            this.shipWorkflow.step = 4
            await this.startPollingShipment(verifyId || error.payment_intent.id)
            return
          }
          throw new Error(error.message)
        }

        if (paymentIntent && paymentIntent.status === 'succeeded') {
          this.shipWorkflow.stripe.paymentIntentId = paymentIntent.id
          this.shipWorkflow.step = 4
          await this.startPollingShipment(verifyId || paymentIntent.id)
        }
      } catch (e) {
        this.shipWorkflow.stripe.error = e.message
        this.shipWorkflow.stripe.processing = false
      }
    },

    // --- Vérifie le paiement puis déclenche la génération de l'étiquette UGO ---
    async startPollingShipment(paymentId) {
      let attempts = 0
      const maxAttempts = 10

      this.shipWorkflow.pollingInterval = setInterval(async () => {
        attempts++
        try {
          const response = await axios.post(`/payments/${paymentId}/verify`)
          const payload = response.data

          if (payload.success) {
            clearInterval(this.shipWorkflow.pollingInterval)
            this.shipWorkflow.pollingInterval = null
            await this.shipParcel()
            return
          }

          if (attempts >= maxAttempts) {
            clearInterval(this.shipWorkflow.pollingInterval)
            this.shipWorkflow.stripe.error = payload.message || payload.error || "Le paiement n'a pas pu être confirmé."
            this.shipWorkflow.stripe.processing = false
          }
        } catch (e) {
          const status = e.response?.status
          if (status && status !== 404 && status !== 402) {
            clearInterval(this.shipWorkflow.pollingInterval)
            const errorData = e.response?.data
            const validationDetails = errorData?.errors ? ' : ' + Object.values(errorData.errors).flat().join(', ') : ''
            this.shipWorkflow.stripe.error = `Oups, impossible de confirmer le paiement. ${errorData?.message || errorData?.error || e.message}${validationDetails}`
            this.shipWorkflow.stripe.processing = false
          } else if (attempts >= maxAttempts) {
            clearInterval(this.shipWorkflow.pollingInterval)
            this.shipWorkflow.stripe.error = "Le paiement prend du temps à être confirmé. Réessayez dans quelques instants."
            this.shipWorkflow.stripe.processing = false
          }
        }
      }, 3000)
    },

    // --- POST /ugo/shipments/{id}/ship : génère le(s) numéro(s) de suivi et étiquette(s) ---
    async shipParcel() {
      const shipmentId = this.shipWorkflow.shipment?.uuid || this.shipWorkflow.shipment?.id
      if (!shipmentId) {
        this.shipWorkflow.stripe.error = "Expédition introuvable, impossible de générer l'étiquette."
        this.shipWorkflow.stripe.processing = false
        return
      }

      this.shipWorkflow.stripe.error = null
      this.shipWorkflow.stripe.processing = true

      let attempts = 0
      const maxAttempts = 5

      // Offre choisie : normalement le backend l'a mémorisée via select-method,
      // mais on la renvoie en secours (utile si on arrive ici sans être repassé
      // par l'étape 2, ex. paiement déjà fait) pour résoudre shipment.serviceId.
      const offer = this.shipWorkflow.selectedMethod?.raw || this.shipWorkflow.shipment?.selected_offer || null
      const carrierUuid = offer?.carrier?.uuid || offer?.carrier_uuid || offer?.service_uuid || null

      const attemptShip = async () => {
        attempts++
        try {
          const payload = {
            ship_to: this.buildAddressPayload(),
            ship_from: this.buildFromAddressPayload(),
            products: (this.shipWorkflow.colis || []).map(c => ({
              currency: 'EUR',
              number: c.nombre || 1,
              value: c.valeur || 0,
              description: c.description,
              hs_code: c.hs_code || undefined
            })),
            validate_first: true,
            ...(offer ? { offer } : {}),
            ...(carrierUuid ? { carrier_uuid: carrierUuid } : {})
          }

          const res = await axios.post(`/ugo/shipments/${shipmentId}/ship`, payload)
          const data = res.data

          if (!data?.success) {
            const err = new Error(data?.error || data?.message || "Erreur lors de la génération de l'étiquette.")
            err.response = { status: 422, data }
            throw err
          }

          const trackingNumbers = data.tracking_numbers || (data.tracking_number ? [data.tracking_number] : [])
          const labelUrls = data.label_urls || (data.label_url ? [data.label_url] : [])

          this.shipWorkflow.shipment = {
            ...this.shipWorkflow.shipment,
            status: data.shipment?.status || 'shipped',
            tracking_number: trackingNumbers[0] || null,
            tracking_numbers: trackingNumbers,
            label_url: labelUrls[0] || null,
            label_urls: labelUrls,
            public_tracking_url: data.public_tracking_url || null,
            is_test: data.shipment?.is_test ?? data.is_test
          }

          this.shipWorkflow.tracking = {
            tracking_number: trackingNumbers[0] || null,
            label_url: labelUrls[0] || null,
            statuses: [],
            notFound: false
          }

          this.shipWorkflow.stripe.processing = false
          this.refreshHostData()
          this.getTracking()
        } catch (e) {
          const status = e.response?.status
          // 402 : paiement pas encore reconnu côté UGO — on retente après un court délai
          if (status === 402 && attempts < maxAttempts) {
            setTimeout(attemptShip, 3000)
            return
          }

          // 409 : déjà expédié côté backend — on récupère simplement le suivi
          if (status === 409) {
            this.shipWorkflow.stripe.processing = false
            this.getTracking()
            return
          }

          console.error('🚨 Erreur lors de la génération de l\'étiquette UGO:', e)
          const errorData = e.response?.data

          // 402 définitif : `detail` porte l'explication compréhensible par le client
          // (ex: compte transporteur à créditer) ; `message` ("Payment is required")
          // n'est qu'un code générique, on ne l'affiche qu'en dernier recours.
          if (status === 402) {
            this.shipWorkflow.stripe.error = errorData?.detail || errorData?.message || 'Payment is required'
            this.shipWorkflow.stripe.processing = false
            return
          }

          // Violations UGO ({ data: { details: [...] } }) ou validation Laravel ({ errors: {champ: [...]} })
          const details = errorData?.data?.details?.map(d => `${d.property_path}: ${d.violation}`).join(', ')
          const laravelErrors = errorData?.errors ? Object.values(errorData.errors).flat().join(', ') : ''
          this.shipWorkflow.stripe.error = [
            errorData?.error || errorData?.message || e.message,
            details || laravelErrors
          ].filter(Boolean).join(' — ')
          this.shipWorkflow.stripe.processing = false
        }
      }

      await attemptShip()
    },

    formatPrice(val, currency = 'EUR', showOriginal = true) {
      const cur = currency || 'EUR'
      let amount = val
      let isCFA = false

      if (cur === 'XOF' || cur === 'XAF' || cur === 'CFA' || (amount > 500 && cur === 'EUR')) {
        isCFA = true
      }

      const euroFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })
      const cfaFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 })

      if (isCFA) {
        const euroValue = amount / 655.957
        if (showOriginal) {
          return `${cfaFormatter.format(amount)} (${euroFormatter.format(euroValue)})`
        }
        return euroFormatter.format(euroValue)
      }

      return euroFormatter.format(amount)
    },

    getCarrierName(shipment) {
      if (!shipment) return 'En attente'

      // Source fiable renvoyée par le backend (persiste après rechargement)
      if (shipment.ugo_carrier_name) return shipment.ugo_carrier_name
      if (shipment.selected_offer?.carrier_name) return shipment.selected_offer.carrier_name

      if (this.shipWorkflow.selectedMethod?.name) return this.shipWorkflow.selectedMethod.name
      if (this.shipWorkflow.selectedMethod?.carrier) return this.shipWorkflow.selectedMethod.carrier

      const methodId = String(shipment.shipping_method_id || '').trim()
      const optionId = String(shipment.shipping_option_id || '').trim()
      const carrierId = String(shipment.carrier || '').trim()

      const id = (methodId || optionId || carrierId).toLowerCase()
      if (!id || id === 'null') return 'Non défini'

      const common = {
        '8': 'Colissimo',
        'colissimo': 'Colissimo',
        'dhl': 'DHL Express',
        'chronopost': 'Chronopost',
        'mondial_relay': 'Mondial Relay',
        'ups': 'UPS',
        'colis_prive': 'Colis Privé',
        'relais_colis': 'Relais Colis',
        'gls': 'GLS'
      }

      if (common[id]) return common[id]
      if (common[methodId]) return common[methodId]
      if (common[optionId]) return common[optionId]

      const cleanId = id.includes(':') ? id.split(':')[1] : id
      const formatted = cleanId.split(/[-_]/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')

      return formatted || id || 'Transporteur'
    },

    // --- Ouvre une URL d'étiquette.
    // Les URLs servies par notre API (sim-label, waybill) exigent le header
    // Authorization. On les ré-écrit en CHEMIN RELATIF pour qu'elles repassent par
    // axios (baseURL correcte + Bearer via l'intercepteur) — peu importe l'APP_URL
    // configuré côté backend (ngrok, localhost, prod…). Les URLs externes (vrais
    // PDF UGO en prod) s'ouvrent directement.
    async openLabelUrl(url) {
      if (!url) return

      const apiPath = String(url).match(/\/((?:api\/)?ugo\/shipments\/[^?#]+)/)
      if (apiPath) {
        const relative = '/' + apiPath[1].replace(/^api\//, '')
        try {
          const res = await axios.get(relative, { responseType: 'blob' })
          const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: res.data?.type || 'application/pdf' }))
          window.open(blobUrl, '_blank')
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000)
        } catch (e) {
          console.error('Téléchargement étiquette échoué:', e)
          this.showToast("Impossible de télécharger l'étiquette pour le moment.", 'error')
        }
        return
      }

      window.open(url, '_blank')
    },

    // --- Télécharge/ouvre la ou les étiquette(s) ; fallback GET /waybill si aucune URL ---
    async downloadLabel() {
      const shipmentId = this.shipWorkflow.shipment?.uuid || this.shipWorkflow.shipment?.id
      if (!shipmentId) return

      try {
        const urls = this.shipWorkflow.shipment?.label_urls?.length
          ? this.shipWorkflow.shipment.label_urls
          : (this.shipWorkflow.tracking?.label_url ? [this.shipWorkflow.tracking.label_url] : [])

        if (urls.length > 0) {
          for (let i = 0; i < urls.length; i++) {
            await this.openLabelUrl(urls[i])
            if (i < urls.length - 1) await new Promise(r => setTimeout(r, 400))
          }
          return
        }

        const res = await axios.get(`/ugo/shipments/${shipmentId}/waybill`, { responseType: 'blob' })
        const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        window.open(blobUrl, '_blank')
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000)
      } catch (e) {
        console.error('Erreur lors du téléchargement de l\'étiquette:', e)
        this.showToast("Impossible de télécharger l'étiquette pour le moment.", 'error')
      }
    },

    // --- GET /ugo/shipments/{id} + GET /ugo/shipments/{id}/tracking ---
    async getTracking() {
      const shipmentId = this.shipWorkflow.shipment?.uuid || this.shipWorkflow.shipment?.id
      if (!shipmentId) return

      try {
        const fullRes = await axios.get(`/ugo/shipments/${shipmentId}`)
        if (fullRes.data?.success && fullRes.data.shipment) {
          const s = fullRes.data.shipment
          const tn = s.tracking_numbers || (s.tracking_number ? [s.tracking_number] : this.shipWorkflow.shipment.tracking_numbers) || []
          const lu = s.label_urls || (s.label_url ? [s.label_url] : this.shipWorkflow.shipment.label_urls) || []

          this.shipWorkflow.shipment = {
            ...this.shipWorkflow.shipment,
            status: s.status || this.shipWorkflow.shipment.status,
            tracking_number: tn[0] || this.shipWorkflow.shipment.tracking_number,
            tracking_numbers: tn,
            label_url: lu[0] || this.shipWorkflow.shipment.label_url,
            label_urls: lu,
            uuid: s.uuid || s.id || this.shipWorkflow.shipment.uuid,
            public_tracking_url: s.public_tracking_url || this.shipWorkflow.shipment.public_tracking_url,
            shipping_method_id: s.shipping_method_id || s.shipping_option_id || this.shipWorkflow.shipment.shipping_method_id,
            ugo_carrier_name: s.ugo_carrier_name || this.shipWorkflow.shipment.ugo_carrier_name,
            selected_offer: s.selected_offer || this.shipWorkflow.shipment.selected_offer,
            is_test: s.is_test ?? this.shipWorkflow.shipment.is_test
          }

          if (s.payments) this.shipWorkflow.payments = s.payments

          if (tn[0]) this.shipWorkflow.tracking.tracking_number = tn[0]
          if (lu[0]) this.shipWorkflow.tracking.label_url = lu[0]
        }

        const trackRes = await axios.get(`/ugo/shipments/${shipmentId}/tracking`)
        if (trackRes.data?.success) {
          this.shipWorkflow.tracking.notFound = false

          // Statut global courant : { status: { label } }
          this.shipWorkflow.tracking.statusLabel =
            trackRes.data.status?.label || trackRes.data.status_label || this.shipWorkflow.tracking.statusLabel || ''

          const events = trackRes.data.events || trackRes.data.tracking?.events || trackRes.data.checkpoints || []
          if (Array.isArray(events) && events.length > 0) {
            this.shipWorkflow.tracking.statuses = events
              .map(ev => ({
                message: ev.label || ev.status || ev.message || 'Statut mis à jour',
                description: ev.description || '',
                location: ev.location || '',
                created_at: ev.date || ev.created_at || ev.timestamp || new Date().toISOString()
              }))
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          }
        }
      } catch (e) {
        // 404 = pas de suivi ; 409 = expédition pas encore "shipped" (ne doit plus
        // arriver dans le flux normal, mais on ne casse pas l'écran pour autant)
        if (e.response?.status === 404 || e.response?.status === 409) {
          this.shipWorkflow.tracking.notFound = true
        } else {
          console.error('Erreur lors de la récupération du suivi UGO:', e)
        }
      }
    },

    showToast(message, type = 'error') {
      this.shipWorkflow.toast.message = message
      this.shipWorkflow.toast.type = type
      this.shipWorkflow.toast.show = true

      setTimeout(() => {
        this.shipWorkflow.toast.show = false
      }, 5000)
    },

    isAlreadyShipped(commande) {
      if (!commande) return false
      const shipment = commande.shipment || commande.commande?.shipment
      return !!shipment
    },

    statusLabel(statut) {
      const labels = {
        0: 'En attente',
        1: 'En cours',
        2: 'Expédié',
        3: 'Livré',
        'ready_to_send': 'Prêt à l\'envoi',
        'picked_up': 'Pris en charge',
        'in_transit': 'En transit',
        'delivered': 'Livré',
        'shipped': 'Expédié',
        'cancelled': 'Annulé',
        'delivery_failed': 'Échec livraison',
        'awaiting_pickup': 'En attente retrait',
        'customs': 'En douane'
      }
      return labels[statut] || statut || 'Inconnu'
    },

    statusClass(statut) {
      const s = String(statut).toLowerCase()
      if (s === '0' || s === 'ready_to_send') return 'is-pending'
      if (s === '1' || s === 'customs' || s === 'awaiting_pickup') return 'is-processing'
      if (s === '2' || s === 'shipped' || s === 'in_transit' || s === 'picked_up') return 'is-shipped'
      if (s === '3' || s === 'delivered') return 'is-delivered'
      if (s === 'cancelled' || s === 'delivery_failed') return 'is-cancelled'
      return ''
    }
  }
}
